import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Link2, Copy, Trash2, Loader2, Plus } from 'lucide-react';
import { useToast } from '@/lib/hooks/use-toast';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { invitationService } from '../services/invitation-service';
import { Invitation } from '../types/invitation.types';
import { customRoleService } from '@/features/permissions/services/custom-role-service';
import type { CustomRole } from '@/features/permissions/types/permission.types';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  AVISO_SOPORTE_NO_PUEDE,
  atributosSoporteNoPuede,
  useEnSoporte,
} from '@/features/mini-prixia/soporte-solo-mira';

const BASE_ROLE_LABELS: Record<string, string> = {
  COMPANY_ADMIN: 'Administrador',
  SALESPERSON: 'Vendedor',
  APPROVER: 'Aprobador',
};
const BASE_ROLES = ['SALESPERSON', 'APPROVER', 'COMPANY_ADMIN'];

const inviteUrl = (token: string) => `${window.location.origin}/invite/${token}`;

export function InvitationLinksCard() {
  // Las acciones que cortan accesos preguntan antes. Ver confirm-dialog.tsx.
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const { toast } = useToast();
  const { user } = useAuth();
  // Soporte sólo mira en Mi empresa: el servidor frena crear y revocar links (SOPORTE_SOLO_MIRA).
  const enSoporte = useEnSoporte();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form: roleValue es un base role ('SALESPERSON') o 'custom:<id>'.
  const [roleValue, setRoleValue] = useState('SALESPERSON');
  const [domain, setDomain] = useState('');

  const ownDomain = user?.email?.split('@')[1] || '';

  const load = () => {
    setLoading(true);
    invitationService
      .list()
      .then(setInvitations)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    customRoleService
      .getAll()
      .then(setCustomRoles)
      .catch(() => {});
  }, []);

  const openDialog = () => {
    setRoleValue('SALESPERSON');
    setDomain(ownDomain);
    setOpen(true);
  };

  const handleCreate = async () => {
    if (!domain.trim()) return;
    setSubmitting(true);
    try {
      const isCustom = roleValue.startsWith('custom:');
      const payload = isCustom
        ? (() => {
            const cr = customRoles.find((c) => c.id === roleValue.slice('custom:'.length));
            return {
              allowedDomain: domain.trim(),
              role: cr?.baseRole || 'SALESPERSON',
              customRoleId: cr?.id,
            };
          })()
        : { allowedDomain: domain.trim(), role: roleValue, customRoleId: null };

      const created = await invitationService.create(payload);
      // 3-nov: no decir «copiado» si el navegador no dejó copiar (http en DEV).
      const copiado = created.token ? await copyToClipboard(inviteUrl(created.token)) : false;
      toast({
        title: 'Link creado',
        description: copiado
          ? 'Copiado al portapapeles. Compártelo con tu equipo.'
          : 'Cópialo desde la lista con «Copiar» y compártelo con tu equipo.',
      });
      setOpen(false);
      load();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error || err?.message || 'No se pudo crear el link',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (token: string) => {
    const enlace = inviteUrl(token);
    if (await copyToClipboard(enlace)) {
      toast({ title: 'Link copiado', description: 'Pégalo donde quieras compartirlo.' });
    } else {
      toast({
        title: 'No se pudo copiar el link',
        description: `Cópialo a mano: ${enlace}`,
        variant: 'destructive',
      });
    }
  };

  const handleRevoke = (id: string, dominio: string) => {
    /*
      Revocar rompe un enlace que quizá ya se repartió por correo: quien lo tenga
      va a encontrarse con que no funciona, sin saber por qué. Conviene preguntar.
    */
    confirm({
      title: `¿Revocar la invitación de ${dominio}?`,
      description:
        'El enlace deja de funcionar para siempre. Si ya se lo compartiste a alguien, va a tener que pedir uno nuevo.',
      confirmText: 'Sí, revocar',
      variant: 'destructive',
      onConfirm: () => aplicarRevoke(id),
    });
  };

  const aplicarRevoke = async (id: string) => {
    try {
      await invitationService.revoke(id);
      toast({ title: 'Link revocado' });
      load();
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err?.response?.data?.error || 'No se pudo revocar',
        variant: 'destructive',
      });
    }
  };

  const roleLabel = (inv: Invitation) =>
    inv.customRole?.name || BASE_ROLE_LABELS[inv.role] || inv.role;

  const activos = invitations.filter((i) => i.isActive);

  return (
    <Card>
      <ConfirmDialog />
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" /> Links de invitación
          </CardTitle>
          <CardDescription>
            Cada link es el pase de UNA persona: sirve una sola vez. Solo un correo del dominio
            permitido podrá crear su cuenta con él, con el rol que elijas. Para sumar a otra
            persona, crea otro link.
          </CardDescription>
        </div>
        <Button onClick={openDialog} size="sm" {...atributosSoporteNoPuede(enSoporte)}>
          <Plus className="h-4 w-4 mr-2" /> Crear link
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : activos.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            No hay links activos. Crea uno por cada persona que quieras invitar.
          </p>
        ) : (
          <div className="space-y-2">
            {activos.map((inv) => (
              <div
                key={inv.id}
                className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary">@{inv.allowedDomain}</Badge>
                    <Badge>{roleLabel(inv)}</Badge>
                    {inv.usedCount >= 1 ? (
                      <Badge variant="outline">Usada</Badge>
                    ) : (
                      <Badge variant="outline">Sin usar</Badge>
                    )}
                  </div>
                  {inv.usedCount >= 1 ? (
                    <div className="text-xs text-muted-foreground mt-1">
                      Este link ya se usó y no se puede volver a usar.
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground truncate mt-1">
                      {inv.token
                        ? inviteUrl(inv.token)
                        : 'El enlace no se muestra en modo soporte.'}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {inv.usedCount < 1 && inv.token && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCopy(inv.token as string)}
                      title="Copiar link"
                      aria-label={`Copiar el enlace de invitación de ${inv.allowedDomain}`}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRevoke(inv.id, inv.allowedDomain)}
                    disabled={enSoporte}
                    title={enSoporte ? AVISO_SOPORTE_NO_PUEDE : 'Revocar'}
                    aria-label={`Revocar la invitación de ${inv.allowedDomain}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear link de invitación</DialogTitle>
            <DialogDescription>
              La primera persona que use este link se registrará en tu empresa con el rol elegido,
              si su correo es del dominio permitido. El link sirve una sola vez.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Rol asignado</Label>
              <select
                value={roleValue}
                onChange={(e) => setRoleValue(e.target.value)}
                className="w-full border border-input bg-background rounded-md px-3 py-2 text-sm"
              >
                <optgroup label="Roles base">
                  {BASE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {BASE_ROLE_LABELS[r]}
                    </option>
                  ))}
                </optgroup>
                {customRoles.length > 0 && (
                  <optgroup label="Roles personalizados">
                    {customRoles.map((cr) => (
                      <option key={cr.id} value={`custom:${cr.id}`}>
                        {cr.name}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Dominio permitido</Label>
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="empresa.com"
              />
              <p className="text-xs text-muted-foreground">
                Solo un correo <strong>@{domain || 'dominio'}</strong> podrá registrarse con este
                link, y una sola persona.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={submitting || !domain.trim()}>
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Crear y copiar link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default InvitationLinksCard;
